import { useState, useEffect } from 'react';
import { apiFetch } from '../../../../config/api';
import { debugError } from '../../../../utils/debugLog';
import {
  getEntitySensitivityLevel,
  getEntityDataType,
  getEntityPattern,
  getEntityDescription,
  getDomainSpecificTestData,
} from '../utils';

/**
 * Custom hook for PolicyDetails component
 *
 * Manages state and data fetching for policy details dialog.
 *
 * @param {Object} policy - Policy object
 * @param {boolean} open - Whether dialog is open
 * @returns {Object} Hook return object with state and functions
 */
export const usePolicyDetails = (policy, open) => {
  const [activeTab, setActiveTab] = useState(0);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testContent, setTestContent] = useState('');
  const [domainDetails, setDomainDetails] = useState(null);
  const [loadingDomainDetails, setLoadingDomainDetails] = useState(false);

  // Fetch domain details when dialog opens
  useEffect(() => {
    if (open && policy) {
      setTestResult(null);
      setTesting(false);
      setActiveTab(0); // Reset to first tab

      // Initialize test content with domain-specific default value
      const domainName = policy.name.replace('-policy', '');
      const testData = getDomainSpecificTestData(domainName);
      setTestContent(testData.message);

      // Fetch domain details for rules and data fields
      fetchDomainDetails(domainName);
    }
  }, [open, policy]);

  const fetchDomainDetails = async domainName => {
    if (!domainName) return;

    setLoadingDomainDetails(true);
    try {
      const response = await apiFetch('/api/v1/content-check/domains');
      if (response.success && response.domains) {
        const domain = response.domains.find(d => d.name === domainName);
        if (domain) {
          setDomainDetails(domain);
        }
      }
    } catch (error) {
      debugError('Error fetching domain details:', error);
    } finally {
      setLoadingDomainDetails(false);
    }
  };

  // Transform supported entities into rules format
  const getPolicyRules = () => {
    if (!domainDetails || !domainDetails.supported_entities) {
      return policy.rules || [];
    }

    // Map entities to rules format
    return domainDetails.supported_entities.map(entity => {
      const sensitivityLevel = getEntitySensitivityLevel(entity);

      return {
        name: entity.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        data_type: getEntityDataType(entity),
        sensitivity_level: sensitivityLevel,
        pattern: getEntityPattern(entity),
        description: getEntityDescription(entity),
      };
    });
  };

  // Get data fields from supported entities
  const getDataFields = () => {
    if (domainDetails && domainDetails.supported_entities) {
      return domainDetails.supported_entities;
    }
    return policy.data_fields || policy.supported_entities || [];
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleTestPolicy = async () => {
    if (!policy) return;

    setTesting(true);
    try {
      // Extract domain name from policy name (e.g., "pii-policy" -> "pii")
      const domainName = policy.name.replace('-policy', '');

      // Get domain-specific test data
      const testData = getDomainSpecificTestData(domainName);

      const response = await apiFetch(
        '/api/v1/content-check-audit/check-with-audit',
        {
          method: 'POST',
          body: JSON.stringify({
            content: testContent, // Use the editable test content
            domains: [domainName],
            reference_id: `policy-test-${Date.now()}`,
            reference_type: 'policy_test',
            reference_metadata: {
              description: testData.description,
              expected_entities: testData.expectedEntities,
            },
          }),
        }
      );

      setTestResult(response);
    } catch (error) {
      debugError('Error testing policy:', error);
      setTestResult({ success: false, error: 'Failed to test policy' });
    } finally {
      setTesting(false);
    }
  };

  return {
    activeTab,
    testResult,
    testing,
    testContent,
    domainDetails,
    loadingDomainDetails,
    handleTabChange,
    handleTestPolicy,
    setTestContent,
    getPolicyRules,
    getDataFields,
  };
};
